import React from 'react';

/**
 * Reusable overlay template component with minimize/expand functionality
 *
 * @param {boolean} isOpen - Whether the overlay is open
 * @param {boolean} isMinimized - Whether the overlay is minimized
 * @param {function} onClose - Function to close the overlay
 * @param {function} onToggleMinimize - Function to toggle minimize state
 * @param {string} title - Main title for the overlay
 * @param {string} subtitle - Optional subtitle text
 * @param {ReactNode} icon - Icon to display in header and minimized tab
 * @param {string} minimizedTabText - Text to show in minimized tab
 * @param {string} width - Width of the overlay (e.g., '640px', '800px')
 * @param {ReactNode} headerActions - Optional additional actions in the header
 * @param {ReactNode} children - Main content of the overlay
 * @param {boolean} showBackdrop - Whether to show backdrop when not minimized (default: true)
 * @param {number} zIndex - Z-index for stacking multiple overlays (default: 50)
 * @param {string} minimizedTabPosition - CSS top position for minimized tab (default: '50%')
 */
const OverlayTemplate = ({
  isOpen,
  isMinimized,
  onClose,
  onToggleMinimize,
  title,
  subtitle = null,
  icon,
  minimizedTabText = 'Tools',
  width = '640px',
  headerActions = null,
  children,
  showBackdrop = true,
  zIndex = 50,
  minimizedTabPosition = '50%'
}) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - only show when not minimized */}
      {!isMinimized && showBackdrop && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          style={{ zIndex: zIndex - 10 }}
          onClick={onClose}
        />
      )}

      {/* Minimized Tab */}
      {isMinimized && (
        <div
          onClick={onToggleMinimize}
          className="fixed right-0 transform -translate-y-1/2 bg-primary-600 dark:bg-primary-700 text-white rounded-l-lg shadow-lg cursor-pointer hover:bg-primary-700 dark:hover:bg-primary-600 transition-all group"
          style={{ width: '40px', height: '120px', top: minimizedTabPosition, zIndex }}
        >
          <div className="flex flex-col items-center justify-center h-full gap-2">
            {icon}
            <div className="text-xs font-medium" style={{ writingMode: 'vertical-rl' }}>
              {minimizedTabText}
            </div>
          </div>
        </div>
      )}

      {/* Overlay Panel */}
      <div
        className={`fixed right-0 top-0 h-full bg-ui-light-background/95 dark:bg-ui-dark-background/95 backdrop-blur-sm shadow-2xl transition-all duration-300 ease-in-out flex flex-col ${
          isMinimized ? 'translate-x-full' : 'translate-x-0'
        }`}
        style={{ width, maxWidth: '90vw', zIndex }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-ui-light-border dark:border-ui-dark-border bg-ui-light-card/95 dark:bg-ui-dark-card/95 backdrop-blur-sm flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
              {icon}
            </div>
            <div>
              <h2 className="text-xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
                {title}
              </h2>
              {subtitle && (
                <p className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Custom header actions */}
            {headerActions}

            {/* Hide Button */}
            <button
              onClick={onToggleMinimize}
              className="p-2 rounded-md text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
              title="Hide"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 rounded-md text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>
    </>
  );
};

export default OverlayTemplate;
